import React, { useState } from "react";
import { Box } from "@mui/material";
import { useWindowSize } from "@uidotdev/usehooks";
import SabiasQue from "../SabiasQue";
import ImagenNoDisponibleKawaii from "../ImagenNoDisponibleKawaii";

// Template55: Página de Shibuya de Tokio
// Copia DIRECTA del componente PageShibuya.jsx de guia-tokio, adaptado para usar las props del sistema guia
const Template55 = ({
	backgroundColor,
	textColor,
	headerBackgroundColor,
	headerTextColor,
	fontFamily,
	page,
	updatePage,
}) => {
	const size = useWindowSize();
	const isSmallScreen = size.width <= 600;
	const [takeshitaError, setTakeshitaError] = useState(false);
	const [kiddyLandError, setKiddyLandError] = useState(false);
	const [crepeError, setCrepeError] = useState(false);
	const [shibuyaError, setShibuyaError] = useState(false);
	const [marioError, setMarioError] = useState(false);
	const [shibuyaStickerError, setShibuyaStickerError] = useState(false);
	const [takeshiteStickerError, setTakeshiteStickerError] = useState(false);
	const [logoError, setLogoError] = useState(false);

	const headerText = page.texts?.[0]?.content;
	const logoImage = page.canvasImages?.find(
		(img) => img.id === "logo",
	)?.imageUrl;

	// Lógica especial para la guía de Tokio: header #24221a en primera página, #e24217 en las siguientes
	const pageOrder =
		page.order !== undefined
			? page.order
			: page.data?.order !== undefined
				? page.data.order
				: -1;
	const isTokioGuide = headerBackgroundColor === "#24221a";
	const actualHeaderColor =
		isTokioGuide && pageOrder > 0 ? "#e24217" : headerBackgroundColor;

	const getImageSrc = (id) => {
		if (page.images && Array.isArray(page.images)) {
			const found = page.images.find((img) => img?.id === id);
			if (found && found.src) return found.src;
		}
		return undefined;
	};

	const getCanvasImageUrl = (id) => {
		if (page.canvasImages && Array.isArray(page.canvasImages)) {
			const found = page.canvasImages.find((img) => img?.id === id);
			if (found && found.imageUrl) return found.imageUrl;
		}
		return undefined;
	};

	const shibuyaSticker = getCanvasImageUrl("shibuya-sticker");
	const takeshiteSticker = getCanvasImageUrl("takeshite-st");
	const kiddyLandImage = getImageSrc("kiddy-land");
	const marioImage = getImageSrc("mario-bros");
	const takeshitaImage = getImageSrc("takeshita");
	const crepeImage = getImageSrc("crepe");
	const shibuyaImage = getImageSrc("shibuya");

	const introText = page.texts?.[1]?.content;
	const takeshitaTitle = page.texts?.[2]?.content;
	const takeshitaText = page.texts?.[3]?.content;
	const omotesandoTitle = page.texts?.[4]?.content;
	const omotesandoText = page.texts?.[5]?.content;
	const shibuyaTitle = page.texts?.[6]?.content;
	const shibuyaText = page.texts?.[7]?.content;
	const stickerKiddyLandText = page.texts?.[8]?.content;
	const stickerCrepeShort = page.texts?.[9]?.content;
	const stickerCrepeLong = page.texts?.[10]?.content;
	const cajaHarajukuText = page.texts?.[11]?.content;
	const stickerKartText = page.texts?.[12]?.content;
	const sabiasQueTakeshitaHtml = page.texts?.[13]?.content;
	const parseSabiasQueItem = (html) => {
		if (!html) return null;
		const parser = new DOMParser();
		const doc = parser.parseFromString(html, "text/html");
		const p = doc.querySelector("p");
		if (!p) return null;
		const text = (p.textContent ?? p.innerText ?? "").trim();
		const emojiMatch = text.match(/^([^\s]+)\s+(.*)/s);
		if (emojiMatch)
			return { icono: emojiMatch[1], texto: emojiMatch[2].trim() };
		return null;
	};
	const sabiasQueTakeshita = parseSabiasQueItem(sabiasQueTakeshitaHtml);
	const contentColor = textColor || "#444";

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				minHeight: "100%",
				height: "auto",
				width: "100%",
				backgroundColor,
				color: contentColor,
				position: "relative",
				fontFamily: `${fontFamily} !important`,
				"& *:not([data-template-header])": {
					fontFamily: `${fontFamily} !important`,
					color: "inherit",
				},
				"& p": {
					marginTop: "0px !important",
					marginBottom: "10px !important",
				},
			}}
		>
			{/* CABECERA */}
			<Box
				data-block-id="header"
				data-template-header
				sx={{
					backgroundColor: actualHeaderColor,
					color: headerTextColor,
					textAlign: "center",
					padding: "15px 0",
					fontFamily: `${fontFamily}`,
					fontSize: "14px",
					fontWeight: 800,
					flexShrink: 0,
					position: "sticky",
					top: 0,
					zIndex: 9999,
				}}
			>
				{headerText}
				{logoImage && !logoError && (
					<Box
						component="img"
						src={logoImage}
						alt=""
						onError={() => setLogoError(true)}
						sx={{
							width: isSmallScreen ? 66 : 80,
							position: "absolute",
							right: 2,
							top: 18,
							zIndex: 999999,
						}}
					/>
				)}
			</Box>

			<Box
				data-block-id="text-1"
				sx={{
					padding: "10px 12px",
					flex: 1,
					position: "relative",
				}}
			>
				{introText && (
					<Box
						component="div"
						dangerouslySetInnerHTML={{ __html: introText }}
						sx={{
							fontSize: 13,
							fontFamily: `${fontFamily} !important`,
							color: contentColor,
						}}
					/>
				)}
			</Box>

			<Box
				sx={{
					display: "flex",
					justifyContent: "flex-start",
					flexDirection: "column",
					minHeight: "100%",
					height: "auto",
				}}
			>
				<Box
					sx={{
						padding: "10px 12px",
						marginBottom: "70px",
						paddingTop: "0px",
					}}
				>
					<Box
						sx={{
							flex: 1,
							display: "flex",
							flexDirection: "column",
							gap: "16px",
						}}
					>
						{/* Takeshita Dori */}
						<Box
							data-block-id="text-2"
							sx={{
								flex: 1,
								display: "flex",
								flexDirection: "column",
								gap: "16px",
								position: "relative",
							}}
						>
							<Box
								sx={{
									display: "flex",
									gap: "20px",
									alignItems: "flex-start",
								}}
							>
								{takeshitaImage && !takeshitaError ? (
									<img
										src={takeshitaImage}
										alt="Takeshita"
										onError={() => setTakeshitaError(true)}
										style={{
											width: "40%",
											height: 110,
											borderRadius: "12px",
											objectFit: "cover",
										}}
									/>
								) : (
									<ImagenNoDisponibleKawaii
										width="40%"
										height={110}
										fontFamily={fontFamily}
										compact
									/>
								)}

								<Box sx={{ width: "60%" }}>
									<Box
										component="h3"
										sx={{
											fontFamily: `${fontFamily} !important`,
											marginBottom: "4px",
											fontSize: 14,
											fontWeight: 700,
											textTransform: "uppercase",
											marginTop: "8px",
										}}
									>
										{takeshitaTitle}
									</Box>
									{takeshitaText && (
										<Box
											component="div"
											dangerouslySetInnerHTML={{ __html: takeshitaText }}
											sx={{
												fontSize: 13,
												color: contentColor,
												fontFamily: `${fontFamily} !important`,
												margin: 0,
											}}
										/>
									)}
									{sabiasQueTakeshita && (
										<Box sx={{ position: "absolute", top: -10, left: "35%" }}>
											<SabiasQue
												icono={sabiasQueTakeshita.icono}
												texto={sabiasQueTakeshita.texto}
											/>
										</Box>
									)}
								</Box>
							</Box>
						</Box>

						{/* Omotesando */}
						<Box
							data-block-id="text-3"
							sx={{
								flex: 1,
								display: "flex",
								flexDirection: "column",
								gap: "16px",
								position: "relative",
							}}
						>
							{takeshiteSticker && !takeshiteStickerError && (
								<img
									src={takeshiteSticker}
									alt=""
									onError={() => setTakeshiteStickerError(true)}
									style={{
										width: 95,
										position: "absolute",
										bottom: -80,
										left: 0,
									}}
								/>
							)}

							{stickerKiddyLandText && (
								<Box
									sx={{
										position: "absolute",
										bottom: isSmallScreen ? -60 : -35,
										left: "80px",
										backgroundColor: "#fff8db",
										border: "2px solid #f5c400",
										borderRadius: "16px 16px 16px 0px",
										padding: "10px 12px",
										fontSize: 11,
										fontFamily: `${fontFamily} !important`,
										color: contentColor,
										boxShadow: "2px 2px 4px rgba(0,0,0,0.15)",
										maxWidth: isSmallScreen ? 160 : 160,
									}}
									component="div"
									dangerouslySetInnerHTML={{ __html: stickerKiddyLandText }}
								/>
							)}

							{crepeImage &&
								!crepeError &&
								(isSmallScreen ? (
									<img
										src={crepeImage}
										alt=""
										onError={() => setCrepeError(true)}
										style={{
											width: 95,
											position: "absolute",
											top: -40,
											left: -30,
										}}
									/>
								) : (
									<img
										src={crepeImage}
										alt=""
										onError={() => setCrepeError(true)}
										style={{
											width: 95,
											position: "absolute",
											bottom: -52,
											right: 0,
										}}
									/>
								))}

							{(stickerCrepeShort || stickerCrepeLong) && (
								<Box
									sx={{
										position: "absolute",
										bottom: isSmallScreen ? 120 : -35,
										left: isSmallScreen ? "20px" : "auto",
										right: isSmallScreen ? "auto" : "75px",
										backgroundColor: "#fff8db",
										border: "2px solid #f5c400",
										borderRadius: isSmallScreen
											? "16px 16px 16px 0px"
											: "16px 16px 0px 16px",
										padding: "10px 12px",
										fontSize: 11,
										fontFamily: `${fontFamily} !important`,
										color: contentColor,
										boxShadow: "2px 2px 4px rgba(0,0,0,0.15)",
										maxWidth: isSmallScreen ? 100 : 160,
									}}
									component="div"
									dangerouslySetInnerHTML={{
										__html:
											(isSmallScreen ? stickerCrepeShort : stickerCrepeLong) ??
											"",
									}}
								/>
							)}
							<Box
								sx={{
									display: "flex",
									gap: "20px",
									alignItems: "flex-start",
								}}
							>
								{kiddyLandImage && !kiddyLandError ? (
									<img
										src={kiddyLandImage}
										alt="KiddyLand"
										onError={() => setKiddyLandError(true)}
										style={{
											width: "40%",
											height: 110,
											borderRadius: "12px",
											objectFit: "cover",
										}}
									/>
								) : (
									<ImagenNoDisponibleKawaii
										width="40%"
										height={110}
										fontFamily={fontFamily}
										compact
									/>
								)}

								<Box sx={{ width: "60%", position: "relative" }}>
									<Box
										component="h3"
										sx={{
											fontFamily: `${fontFamily} !important`,
											marginBottom: "4px",
											fontSize: 14,
											fontWeight: 700,
											textTransform: "uppercase",
											marginTop: "8px",
										}}
									>
										{omotesandoTitle}
									</Box>
									{omotesandoText && (
										<Box
											component="div"
											dangerouslySetInnerHTML={{ __html: omotesandoText }}
											sx={{
												fontSize: 13,
												color: contentColor,
												fontFamily: `${fontFamily} !important`,
												margin: 0,
											}}
										/>
									)}
								</Box>
							</Box>
						</Box>
					</Box>
				</Box>

				{cajaHarajukuText && (
					<Box
						sx={{
							padding: "10px 12px",
							position: "relative",
							marginTop: isSmallScreen ? "0px" : 0,
						}}
					>
						<Box
							sx={{
								backgroundColor: "#ffe3cc",
								border: "3px solid #f28b35",
								borderRadius: "12px",
								padding: "8px 14px",
								fontSize: "12px",
								color: contentColor,
								textAlign: "center",
								fontFamily: `${fontFamily} !important`,
							}}
						>
							{cajaHarajukuText}
						</Box>
					</Box>
				)}

				{/* Shibuya */}
				<Box data-block-id="text-4" sx={{ position: "relative" }}>
					<Box sx={{ width: "100%" }}>
						<Box sx={{ padding: "10px 12px", position: "relative" }}>
							<Box
								component="h3"
								sx={{
									fontFamily: `${fontFamily} !important`,
									fontSize: 14,
									fontWeight: 700,
									textTransform: "uppercase",
									flex: 1,
									margin: 0,
								}}
							>
								{shibuyaTitle}
							</Box>
							{shibuyaText && (
								<Box
									id="shibuya-text-55"
									component="div"
									dangerouslySetInnerHTML={{ __html: shibuyaText }}
									sx={{
										fontSize: 13,
										color: contentColor,
										fontFamily: `${fontFamily} !important`,
										margin: 0,
										flex: 1,
										marginBottom: "0px !important",
										"& p": {
											marginBottom: "0px !important",
										},
									}}
								/>
							)}
						</Box>
						{shibuyaSticker && !shibuyaStickerError && (
							<img
								src={shibuyaSticker}
								alt=""
								onError={() => setShibuyaStickerError(true)}
								style={{
									width: 100,
									position: "absolute",
									bottom: 20,
									right: 8,
								}}
							/>
						)}
						{marioImage && !marioError && (
							<img
								src={marioImage}
								alt=""
								onError={() => setMarioError(true)}
								style={{
									width: 110,
									position: "absolute",
									bottom: 10,
									left: -10,
								}}
							/>
						)}
						{stickerKartText && (
							<Box
								sx={{
									position: "absolute",
									bottom: "60px",
									left: "80px",
									backgroundColor: "#fff8db",
									border: "2px solid #f5c400",
									borderRadius: "16px 16px 16px 0px",
									padding: "10px 12px",
									fontSize: 11,
									fontFamily: `${fontFamily} !important`,
									color: contentColor,
									boxShadow: "2px 2px 4px rgba(0,0,0,0.15)",
									maxWidth: 200,
									"& a": {
										color: "#f28b35",
										fontWeight: 600,
										textDecoration: "underline",
									},
								}}
								component="div"
								dangerouslySetInnerHTML={{ __html: stickerKartText }}
							/>
						)}
					</Box>
					{shibuyaImage && !shibuyaError ? (
						<img
							src={shibuyaImage}
							alt="Shibuya"
							onError={() => setShibuyaError(true)}
							style={{
								width: "100%",
								height: 140,
								objectFit: "cover",
							}}
						/>
					) : (
						<ImagenNoDisponibleKawaii
							width="100%"
							height={140}
							fontFamily={fontFamily}
						/>
					)}
				</Box>
			</Box>
		</Box>
	);
};

Template55.thumbnail = "/images/template-1.png";

export default Template55;
